import { get, post, put, del } from "@/lib/api";
import type {
  CompanyInfo,
  StatusResponse,
  DashboardItem,
  DashboardParams,
  TaskItem,
  ShiftItem,
  ShiftParams,
  ChatRoomListItem,
  ChatMessageListResponse,
  SendChatMessageRequest,
  SendChatMessageResponse,
  EditChatMessageRequest,
  ChatUploadURLRequest,
  ChatUploadURLResponse,
  StatsResponse,
  StatsParams,
  StatsMonthlyResponse,
  StatsMonthlyParams,
  NotificationUpdateResponse,
  LocationRequest,
  DistanceRefreshParams,
  DistanceRefreshResponse,
} from "@/types/driver_api_def";

// ■■■■■■■ API通信層 ■■■■■■■

// GET /api/driver/company-info - 所属企業基礎情報取得
export const getCompanyInfo = async (): Promise<CompanyInfo> => {
  return await get<CompanyInfo>("/api/driver/company-info");
};

// GET /api/driver/dashboard - ダッシュボード情報取得
export const getDashboard = async (params?: DashboardParams): Promise<DashboardItem[]> => {
  return await get<DashboardItem[]>("/api/driver/dashboard", params);
};

// POST /api/driver/task/read/:messageId - メッセージ既読登録
export const postTaskRead = async (messageId: string): Promise<StatusResponse> => {
  return await post<StatusResponse>(`/api/driver/task/read/${messageId}`, {});
};

// GET /api/driver/tasks - タスクボックス情報取得
export const getTasks = async (): Promise<TaskItem[]> => {
  return await get<TaskItem[]>("/api/driver/tasks");
};

// POST /api/driver/task/confirmed/:taskId - タスクの確認アクション
export const postTaskConfirmed = async (taskId: string): Promise<StatusResponse> => {
  return await post<StatusResponse>(`/api/driver/task/confirmed/${taskId}`, {});
};

// POST /api/driver/task/completed/:taskId - 手動イベントを完了にする（#116。確認とは別概念の真の完了）
export const postTaskCompleted = async (taskId: string): Promise<StatusResponse> => {
  return await post<StatusResponse>(`/api/driver/task/completed/${taskId}`, {});
};

// GET /api/driver/shift - シフト情報取得
export const getShift = async (params?: ShiftParams): Promise<ShiftItem[]> => {
  return await get<ShiftItem[]>("/api/driver/shift", params);
};

// ■■■■■■■ チャットAPI ■■■■■■■

export const getChatRooms = async (): Promise<ChatRoomListItem[]> => {
  return await get<ChatRoomListItem[]>("/api/driver/chat/rooms");
};

export const getChatMessages = async (roomId: string, params?: { cursor?: string; direction?: "before" | "after"; limit?: number }): Promise<ChatMessageListResponse> => {
  return await get<ChatMessageListResponse>(`/api/driver/chat/rooms/${roomId}/messages`, params);
};

export const postChatMessage = async (roomId: string, params: SendChatMessageRequest): Promise<SendChatMessageResponse> => {
  return await post<SendChatMessageResponse>(`/api/driver/chat/rooms/${roomId}/messages`, params);
};

export const putChatMessage = async (messageId: string, params: EditChatMessageRequest): Promise<StatusResponse> => {
  return await put<StatusResponse>(`/api/driver/chat/messages/${messageId}`, params);
};

export const deleteChatMessage = async (messageId: string): Promise<StatusResponse> => {
  return await del<StatusResponse>(`/api/driver/chat/messages/${messageId}`);
};

export const postChatRoomRead = async (roomId: string): Promise<StatusResponse> => {
  return await post<StatusResponse>(`/api/driver/chat/rooms/${roomId}/read`, {});
};

export const putChatRoomPin = async (roomId: string): Promise<StatusResponse> => {
  return await put<StatusResponse>(`/api/driver/chat/rooms/${roomId}/pin`, {});
};

export const postChatUploadURL = async (params: ChatUploadURLRequest): Promise<ChatUploadURLResponse> => {
  return await post<ChatUploadURLResponse>("/api/driver/chat/upload-url", params);
};

// GET /api/driver/stats - ドライバー稼働データ取得
export const getStats = async (params?: StatsParams): Promise<StatsResponse> => {
  return await get<StatsResponse>("/api/driver/stats", params);
};

// GET /api/driver/stats/monthly - ドライバー月別日別稼働一覧取得
export const getStatsMonthly = async (params: StatsMonthlyParams): Promise<StatsMonthlyResponse> => {
  return await get<StatsMonthlyResponse>("/api/driver/stats/monthly", params);
};

// POST /api/driver/stats/distance/refresh - 走行距離の再集計（#204）
// 走行の詳細データはDynamoDBにあり毎回の集計は重いため、マイページの更新ボタンからだけ呼ぶ。
export const postStatsDistanceRefresh = async (
  params?: DistanceRefreshParams,
): Promise<DistanceRefreshResponse> => {
  const query = new URLSearchParams();
  if (params?.from) query.set("from", params.from);
  if (params?.to) query.set("to", params.to);
  const suffix = query.toString() ? `?${query.toString()}` : "";
  return await post<DistanceRefreshResponse>(`/api/driver/stats/distance/refresh${suffix}`, {});
};

// POST /api/driver/shift/register - シフト登録
export const postShiftRegister = async (params: {
  shiftType: string;
  availableFrom: string;
  availableTo: string;
  notes: string;
}): Promise<{ status: string; message: string; scheduleId: string }> => {
  return await post<{ status: string; message: string; scheduleId: string }>("/api/driver/shift/register", params);
};

// GET /api/driver/has-incomplete-task - 当日に未完了の割当タスクがあるか（退勤前の警告判定用）
export const getHasIncompleteTask = async (): Promise<{ hasIncompleteTask: boolean }> => {
  return await get<{ hasIncompleteTask: boolean }>("/api/driver/has-incomplete-task");
};

// ■■■■■■■ Notification API通信層 ■■■■■■■

// GET /api/notification/update - 対象ユーザに新規通知があるかを確認する(ロングポーリング形式)
export const getNotificationUpdate = async (): Promise<NotificationUpdateResponse> => {
  return await get<NotificationUpdateResponse>("/api/notification/update");
};

// POST /api/notification/location - ユーザの現在位置をサーバに通知する
export const postLocation = async (params: LocationRequest): Promise<StatusResponse> => {
  return await post<StatusResponse>("/api/notification/location", params);
};

// POST /api/notification/location/stop - 位置トラッキングを停止する（退勤）
export const postLocationStop = async (): Promise<StatusResponse> => {
  return await post<StatusResponse>("/api/notification/location/stop", {});
};
